import { Analytics } from '@cobliteam/analytics';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import { motion } from 'motion/react';
import { AIAssistantThreadSelection } from './AIAssistantThreadSelector';
import AIBetaFlag from './AIBetaFlag';

interface AIAssistantHeaderProps {
  showHeader?: boolean;
  handleGoBack?: () => void;
  resetChat?: boolean;
  isExpanded?: boolean;
  onExpand?: () => void;
  isSidepanelDrawer?: boolean;
  onToggleSidepanelLayout?: () => void;
  hasMessages?: boolean;
  headerActions?: React.ReactNode;
  onNewConversation?: () => void;
}

const AIAssistantHeader = ({
  showHeader = true,
  handleGoBack,
  isExpanded,
  onExpand,
  isSidepanelDrawer,
  onToggleSidepanelLayout,
  hasMessages = false,
  headerActions,
  onNewConversation,
}: AIAssistantHeaderProps) => {
  const { t } = useTranslation('ai');

  if (!(showHeader && (hasMessages || handleGoBack || headerActions || onNewConversation || onToggleSidepanelLayout))) return null;

  const handleExpand = () => {
    const nextExpanded = !isExpanded;
    Analytics.track('AI - Chat - View Mode Toggled', { isExpanded: nextExpanded });
    onExpand?.();
  };

  const handleNewConversation = () => {
    Analytics.track('AI - Chat - Open New Conversation', { source: 'header' });
    onNewConversation?.();
  };

  const handleToggleSidepanelLayout = () => {
    Analytics.track('AI - Sidepanel - Layout Toggled', { layout: isSidepanelDrawer ? 'inline' : 'drawer' });
    onToggleSidepanelLayout?.();
  };

  return (
    <motion.div
      data-testid='ai-assistant-header'
      className='flex items-center border-gray-6 p-2'
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <AIBetaFlag className='ml-1' />
      <div className='flex-1' />
      <Button
        variant='ghost'
        size='sm'
        color='default'
        onClick={handleNewConversation}
        icon={<Icon name='plus' className='h-4 w-4' />}
        disabled={!onNewConversation}
        aria-label={t('assistant.new_conversation')}
        data-testid='ai-header-new-conversation'
      />
      {onToggleSidepanelLayout && (
        <Button
          variant={isSidepanelDrawer ? 'surface' : 'ghost'}
          size='sm'
          color='default'
          onClick={handleToggleSidepanelLayout}
          icon={<Icon name={isSidepanelDrawer ? 'minimize' : 'expand'} className='h-4 w-4' />}
          aria-label={t(isSidepanelDrawer ? 'sidepanel.set_inline_aria_label' : 'sidepanel.set_drawer_aria_label')}
          title={t(isSidepanelDrawer ? 'sidepanel.set_inline_aria_label' : 'sidepanel.set_drawer_aria_label')}
          data-testid='ai-header-toggle-layout'
        />
      )}
      <AIAssistantThreadSelection />

      {onExpand && (
        <Button variant='ghost' size='sm' icon={<Icon name={isExpanded ? 'minimize' : 'expand'} className='h-4 w-4' />} className='h-8 w-8 p-0' onClick={handleExpand} />
      )}
      {headerActions}
    </motion.div>
  );
};

export default AIAssistantHeader;
